import api from '../api';

const getUserFacingErrorMessage = () => 'Google Sign-In failed. Please try again.';

const getDebugMessage = (error) => {
  if (!error) return null;
  if (typeof error === 'string') return error;

  const origin = (() => {
    try {
      return window.location.origin;
    } catch {
      return '';
    }
  })();

  const base = error?.error || error?.detail || error?.message;

  if (typeof base === 'string' && (base.includes('Invalid Google Token') || base.includes('Invalid audience'))) {
    const originHint = origin ? ` Add this origin in Google Cloud -> OAuth client -> Authorized JavaScript origins: ${origin}.` : '';
    return `${base}. This usually means your Google OAuth Web client is not configured for this site.${originHint} Also ensure frontend REACT_APP_GOOGLE_CLIENT_ID matches backend GOOGLE_CLIENT_ID, then restart the React dev server.`;
  }

  if (error?.message === 'Network Error' || error?.code === 'ERR_NETWORK') {
    return 'Cannot reach the backend API (Network Error). Verify the backend is running and `REACT_APP_API_BASE_URL` points to it.';
  }
  if (typeof error?.message === 'string' && error.message.includes('ERR_CONNECTION_REFUSED')) {
    return 'Backend connection refused. Ensure Django is running on the configured API URL.';
  }

  return typeof base === 'string' ? base : null;
};

const handleGoogleSuccess = async (
  credentialResponse,
  navigate,
  setLoading,
  setLoginError,
  rolegiven,
  redirectTo
) => {
  const isDev = process.env.NODE_ENV !== 'production';
  const credential = credentialResponse?.credential;

  if (!credential) {
    if (isDev) {
      console.warn(
        'Google Sign-In did not return a credential. If you are using an in-app browser/webview, try opening this page in Chrome/Edge.'
      );
    }
    setLoginError?.('Google Sign-In could not be completed. Please try again.');
    return;
  }

  try {
    setLoading?.(true);
    setLoginError?.('');

    const data = await api.post('users/auth/google-login/', {
      token: credential,
      role: rolegiven,
    });

    try {
      if (data?.access) localStorage.setItem('access_token', data.access);
      if (data?.refresh) localStorage.setItem('refresh_token', data.refresh);
    } catch {
      // ignore storage errors
    }

    const defaultRedirect = rolegiven === 'club_member'
      ? '/clubmember/welcome'
      : '/student/welcome';
    const target = redirectTo || defaultRedirect;

    if (typeof navigate === 'function') {
      navigate(target, { replace: true });
    } else {
      window.location.replace(target);
    }
  } catch (error) {
    console.error('Google Login Error:', error);

    const debugMessage = getDebugMessage(error?.response?.data || error);
    if (debugMessage) console.error(debugMessage);

    setLoginError?.(getUserFacingErrorMessage());
  } finally {
    setLoading?.(false);
  }
};

export default handleGoogleSuccess;
